import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import * as yup from 'yup';
import { NewDropdown, Typography } from 'app/components/ui';
import { i18n } from 'shared/i18n';
import { useUpdateTag } from 'entities/tag';
import { EditableAccuracyInput } from './EditableAccuracyInput';

interface AccuracyFieldProps {
  tagId: string;
  documentSuperId: number;
  tagDecimals: number | 'INF' | null;
  tagPrecision: number | 'INF' | null;
  configDecimals: number | 'INF' | null;
  configPrecision: number | 'INF' | null;
  preferredType: 'decimals' | 'precision';
  disabled?: boolean;
}

const accuracySchema = yup.object().shape({
  value: yup
    .mixed()
    .transform((_value, original) => {
      if (original === '' || original === null || original === undefined) return null;
      if (String(original).toUpperCase() === 'INF') return 'INF';
      const num = Number(original);
      return isNaN(num) ? original : num;
    })
    .test('valid-accuracy', i18n('tag:accuracy.invalid'), (value) => {
      if (value === null || value === 'INF') return true;
      if (typeof value !== 'number') return false;
      return Number.isInteger(value) && value >= -15 && value <= 15;
    }),
});

function resolveActiveType(
  tagDecimals: number | 'INF' | null,
  tagPrecision: number | 'INF' | null,
  preferredType: 'decimals' | 'precision'
): 'decimals' | 'precision' {
  if (tagPrecision != null && tagDecimals == null) return 'precision';
  if (tagDecimals != null && tagPrecision == null) return 'decimals';
  return preferredType;
}

export const AccuracyField = ({
  tagId,
  documentSuperId,
  tagDecimals,
  tagPrecision,
  configDecimals,
  configPrecision,
  preferredType,
  disabled = false,
}: AccuracyFieldProps) => {
  const { t } = useTranslation('tag');

  const { mutate: updateTag } = useUpdateTag();

  const [activeType, setActiveType] = useState<'decimals' | 'precision'>(() => resolveActiveType(tagDecimals, tagPrecision, preferredType));

  // Only reset when viewing a different tag
  useEffect(() => {
    setActiveType(resolveActiveType(tagDecimals, tagPrecision, preferredType));
  }, [tagId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show tag value, falling back to config default when the tag has no explicit value
  const displayValue = activeType === 'decimals' ? tagDecimals ?? configDecimals : tagPrecision ?? configPrecision;

  const handleTypeChange = (type: string) => {
    const newType = type as 'decimals' | 'precision';
    setActiveType(newType);

    updateTag({
      id: tagId,
      documentSuperId,
      ...(newType === 'decimals' ? { decimals: null, precision: null } : { precision: null, decimals: null }),
    });
  };

  const handleValueSubmit = (value: number | 'INF' | null) => {
    if (activeType === 'decimals') {
      updateTag({ id: tagId, documentSuperId, decimals: value ?? 0 });
    } else {
      if (value === null) return;
      updateTag({ id: tagId, documentSuperId, precision: value });
    }
  };

  const typeOptions = [
    { value: 'decimals', label: t('accuracy.decimals') },
    { value: 'precision', label: t('accuracy.precision') },
  ];

  if (disabled) {
    const typeLabel = typeOptions.find((o) => o.value === activeType)?.label ?? activeType;
    return (
      <div className="property-row">
        <Typography fontWeight="semibold" className="property-label">
          {typeLabel}
        </Typography>
        <Typography variant="body-7" fontWeight="light">
          {displayValue ?? '-'}
        </Typography>
      </div>
    );
  }

  return (
    <div className="property-row">
      <NewDropdown variant="no-border" options={typeOptions} currentValue={activeType} onUpdate={handleTypeChange} />
      <EditableAccuracyInput value={displayValue} onSubmit={handleValueSubmit} validationSchema={accuracySchema} fieldName="value" placeholder="-" />
    </div>
  );
};
